import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import {
  createClaim,
  getClaim,
  getNextClaimNumber,
  listEntityRows,
  listUsers,
} from "../services/apiClient.js";
import ClaimForm from "../components/claims/ClaimForm.jsx";
import { emptyClaimForm, xrefClaimForm, CLAIM_LOOKUP_SOURCES } from "../components/claims/claimFields.js";

// Blank new claim, or — with ?xref=<claimId> — the legacy X-Ref flow: the form
// arrives pre-filled from the source claim (identity fields only; dates, QR
// state and notes stay blank) and the saved claim joins that claim's family.

function NewClaimPage() {
  const [form, setForm] = useState(emptyClaimForm);
  const [opts, setOpts] = useState({});
  const [users, setUsers] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [nextNumber, setNextNumber] = useState(null);
  const [xrefSource, setXrefSource] = useState(null); // { id, claim_number }
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const xrefId = Number(searchParams.get("xref")) || null;

  useEffect(() => {
    Promise.all(CLAIM_LOOKUP_SOURCES.map((k) => listEntityRows(k).then((rows) => [k, rows])))
      .then((pairs) => setOpts(Object.fromEntries(pairs)))
      .catch(() => setError("Unable to load dropdown data."));
    listUsers({ pco: true }).then(setUsers).catch(() => {});
    // Inspector is its OWN list — see ClaimForm's PEOPLE_SOURCES.
    listUsers({ inspectors: true }).then(setInspectors).catch(() => {});
    getNextClaimNumber().then(setNextNumber).catch(() => {});
    if (xrefId) {
      getClaim(xrefId)
        .then(({ claim }) => {
          setForm(xrefClaimForm(claim));
          setXrefSource({ id: claim.id, claim_number: claim.claim_number });
        })
        .catch(() => setError("Unable to load the claim to X-Ref from."));
    }
  }, [xrefId]);

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function save() {
    setError("");
    setSaving(true);
    try {
      const payload = xrefSource ? { ...form, parent_claim_id: xrefSource.id } : form;
      const claim = await createClaim(payload);
      navigate(`/claims/${claim.claim_number}`);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to create claim.");
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          New Claim
          {xrefSource ? (
            <span className="text-muted fs-6 ms-2">
              X-Ref of claim #{xrefSource.claim_number}
            </span>
          ) : null}
        </h4>
        {nextNumber != null ? (
          <span className="text-muted">
            Will be claim #<strong>{nextNumber}</strong>
          </span>
        ) : null}
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      <ClaimForm form={form} onChange={handleChange} opts={opts} users={users} inspectors={inspectors} openAll />

      <Button
        onClick={save}
        disabled={saving || !(form.client_name || "").trim() || !form.job_type_id}
        data-hint={!form.job_type_id ? "Pick a Job Type first — it's required" : undefined}
      >
        Create Claim
      </Button>
      <Button variant="secondary" className="ms-2" onClick={() => navigate("/claims/search")}>
        Cancel
      </Button>
    </div>
  );
}

export default NewClaimPage;
